import { useEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductGallery } from "@/components/ProductGallery";
import { Reveal } from "@/components/Reveal";
import { OrderDialog } from "@/components/OrderDialog";
import { ProductDetailDialog } from "@/components/ProductDetailDialog";
import { DEFAULT_COMPLIANCE_NOTE } from "@/components/ComplianceNote";
import { fetchProducts, formatPHP } from "@/lib/api";
import { CATEGORIES, CATEGORY_BADGE, CATEGORY_LABEL } from "@/data/site";
import { TicketPercent, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const DEFAULT_PRICE_LABEL = "Promo Price";
const DEFAULT_PROMO_NOTE = "Promotional price \u2014 mag-iiba ang presyo anytime soon. I-order na habang promo pa!";

export const Products = ({ content = {}, payment = {} }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [detail, setDetail] = useState(null);
  const [orderFor, setOrderFor] = useState(null);
  const [pendingSlug, setPendingSlug] = useState(null);
  const productsRef = useRef([]);

  useEffect(() => {
    let mounted = true;
    fetchProducts()
      .then((data) => mounted && setProducts(data))
      .catch(() => mounted && setProducts([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Keep a live ref so the (once-registered) event handler always sees latest products.
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  // Open a product's detail dialog when another section (e.g. Lifestyle gallery)
  // requests it via a custom event. Falls back to a pending slug if products
  // have not finished loading yet.
  useEffect(() => {
    const handler = (e) => {
      const slug = e?.detail?.slug;
      if (!slug) return;
      const found = (productsRef.current || []).find((p) => p.slug === slug);
      if (found) setDetail(found);
      else setPendingSlug(slug);
    };
    window.addEventListener("ecentra:open-product", handler);
    return () => window.removeEventListener("ecentra:open-product", handler);
  }, []);

  useEffect(() => {
    if (!pendingSlug || !products.length) return;
    const found = products.find((p) => p.slug === pendingSlug);
    if (found) {
      setDetail(found);
      setPendingSlug(null);
    }
  }, [products, pendingSlug]);

  const filtered = useMemo(
    () => (active === "all" ? products : products.filter((p) => p.category === active)),
    [products, active]
  );

  const openDetail = (p) => setDetail(p);
  const openOrder = (p) => {
    setDetail(null);
    setOrderFor(p || { name: "" });
  };

  const priceLabel = content.priceLabel || DEFAULT_PRICE_LABEL;
  const promoNote = content.promoNote !== undefined ? content.promoNote : DEFAULT_PROMO_NOTE;
  const disclaimer = content.disclaimer !== undefined ? content.disclaimer : DEFAULT_COMPLIANCE_NOTE;

  return (
    <section id="products" className="scroll-anchor bg-secondary/40">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-wider text-accent">{content.eyebrow}</p>
              <h2 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {content.heading}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">{content.body}</p>
            </div>
          </div>
        </Reveal>

        {promoNote ? (
          <Reveal>
            <div
              className="mt-6 flex items-start gap-3 rounded-xl border border-accent/30 bg-accent/10 px-4 py-3"
              data-testid="promo-price-notice"
            >
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <p className="text-sm font-medium leading-relaxed text-foreground">{promoNote}</p>
            </div>
          </Reveal>
        ) : null}

        <div className="mt-8">
          <Tabs value={active} onValueChange={setActive}>
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0" data-testid="products-category-tabs">
              {CATEGORIES.map((c) => (
                <TabsTrigger
                  key={c.value}
                  value={c.value}
                  className="rounded-full border border-border bg-card px-4 py-2 text-sm data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  data-testid={`category-tab-${c.value}`}
                >
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="products-grid">
          {loading &&
            Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="overflow-hidden rounded-xl border-border">
                <Skeleton className="aspect-[4/3] w-full" />
                <div className="space-y-3 p-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </Card>
            ))}

          {!loading &&
            filtered.map((p, i) => (
              <Reveal key={p.slug || p.id} delay={(i % 4) * 0.05}>
                <Card className="group flex h-full flex-col overflow-hidden rounded-xl border-border bg-card shadow-[0_10px_30px_-18px_rgba(15,23,42,0.25)] transition-shadow duration-200 hover:shadow-[0_18px_50px_-26px_rgba(15,23,42,0.35)]">
                  <div className="product-media w-full overflow-hidden bg-gradient-to-br from-secondary/60 to-muted">
                    <ProductGallery
                      images={p.images && p.images.length ? p.images : (p.imageUrl ? [p.imageUrl] : [])}
                      alt={p.name}
                      fallbackLabel={p.line || p.name}
                      ratio={16 / 9}
                      imageClassName="p-3"
                      onImageClick={() => openDetail(p)}
                      testid={`product-gallery-${p.slug}`}
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <span className={cn("inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium", CATEGORY_BADGE[p.category] || CATEGORY_BADGE.apparel)}>
                      {CATEGORY_LABEL[p.category] || p.category}
                    </span>
                    <button onClick={() => openDetail(p)} className="mt-2 text-left">
                      <h3 className="text-base font-semibold leading-snug text-foreground">{p.name}</h3>
                    </button>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.tagline}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-display text-lg font-semibold text-foreground" data-testid={`product-price-${p.slug}`}>
                        {formatPHP(p.pricePHP)}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent/12 px-2 py-0.5 text-[11px] font-semibold text-accent"
                        data-testid={`product-promo-badge-${p.slug}`}
                      >
                        <TicketPercent className="h-3 w-3" /> {priceLabel}
                      </span>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => openDetail(p)} data-testid={`product-details-${p.slug}`}>
                        Details
                      </Button>
                      <Button className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => openOrder(p)} data-testid={`product-order-${p.slug}`}>
                        {payment.gcashEnabled ? "Buy" : "I-order"}
                      </Button>
                    </div>
                  </div>
                </Card>
              </Reveal>
            ))}
        </div>

        {!loading && filtered.length === 0 && (
          <Card className="mt-8 flex flex-col items-center justify-center gap-4 rounded-xl border-dashed p-12 text-center">
            <p className="text-muted-foreground">Wala pang product sa category na ito.</p>
            <Button variant="outline" onClick={() => setActive("all")}>View all</Button>
          </Card>
        )}
      </div>

      <ProductDetailDialog product={detail} open={Boolean(detail)} onClose={() => setDetail(null)} onOrder={openOrder} gcashEnabled={!!payment.gcashEnabled} priceLabel={priceLabel} promoNote={promoNote} disclaimer={disclaimer} />
      <OrderDialog product={orderFor} open={Boolean(orderFor)} onClose={() => setOrderFor(null)} payment={payment} disclaimer={disclaimer} />
    </section>
  );
};
